import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useStore } from '../store/useStore';
import { api, getTokens, API_BASE } from '../lib/api';
import { ensurePushSubscription, pushSupported, isPushSubscribed } from '../lib/push';

/** Cada cuánto le decimos al servidor que seguimos aquí (su TTL es de 90 s). */
const LATIDO_MS = 25000;
/** Sin tocar ratón, teclado ni pantalla en este plazo, la persona no está delante. */
const INACTIVO_MS = 2 * 60 * 1000;

let ultimaActividad = Date.now();

/** ¿La persona está DELANTE de esta pestaña? `visibilityState` solo no basta: una
 *  pestaña sigue «visible» con la pantalla bloqueada, con la persona fuera o con
 *  otro programa encima de la ventana, y entonces el aviso se quedaba en un toast
 *  que nadie veía. Hace falta además foco y actividad reciente. */
function atento() {
  if (typeof document === 'undefined') return false;
  if (document.visibilityState !== 'visible') return false;
  if (typeof document.hasFocus === 'function' && !document.hasFocus()) return false;
  return Date.now() - ultimaActividad < INACTIVO_MS;
}

function playPing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    const ctx = new Ctx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.connect(g); g.connect(ctx.destination);
    o.type = 'sine'; o.frequency.value = 880;
    g.gain.setValueAtTime(0.0001, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.02);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
    o.start(); o.stop(ctx.currentTime + 0.42);
    setTimeout(() => ctx.close(), 600);
  } catch (e) {}
}

export function useNotifications() {
  const qc = useQueryClient();
  const { user, pushNotification, setNotifications, soundEnabled, setWsStatus } = useStore();
  const nav = useNavigate();
  // En refs (M29): como dependencia del efecto, activar/silenciar el sonido cerraba y reabría el
  // WebSocket, y en ese hueco se podía perder un aviso.
  const sonidoRef = useRef(soundEnabled);
  sonidoRef.current = soundEnabled;
  const abrirRef = useRef(null);
  abrirRef.current = (d) => {
    // Solo se abre el correo: cambiar de carpeta o de cuenta borraba la vista en la que se estaba (B12)
    const s = useStore.getState();
    const carpeta = d.folder || 'inbox';
    if (d.accountId && d.uid) s.openMessage(d.accountId, carpeta, String(d.uid));
    nav('/mail');
  };
  const wsRef = useRef(null);
  const pushEnsured = useRef(false);
  // ¿Este navegador recibe Web Push? Si sí, el banner del sistema lo da el service
  // worker y aquí no lanzamos otro: dos avisos por un correo es un error, no celo.
  const pushActivo = useRef(false);

  // load persisted notifications
  // `account_id` es imprescindible: sin él, pulsar la campana solo puede llevar a
  // la bandeja, nunca al correo concreto.
  // Los avisos que no son de correo (calendario, Drive, casos) no llegan por el WebSocket: se vuelven a
  // pedir cada minuto con la pestaña a la vista y al volver a ella, para que la campana no espere a recargar.
  useEffect(() => {
    if (!user) return undefined;
    const cargar = () => api.get('/notifications').then((r) => setNotifications(r.data.map((n) => ({
      id: n.id, read: n.read, title: n.title, subject: n.subject || n.body, uid: n.uid, type: n.type,
      account_id: n.account_id, account_email: n.account_email, folder: n.folder || 'inbox', link: n.link,
    })))).catch(() => {});
    cargar();
    const t = setInterval(() => { if (document.visibilityState === 'visible') cargar(); }, 60000);
    const alVolver = () => { if (document.visibilityState === 'visible') cargar(); };
    document.addEventListener('visibilitychange', alVolver);
    return () => { clearInterval(t); document.removeEventListener('visibilitychange', alVolver); };
  }, [user, setNotifications]);

  useEffect(() => {
    if (!user || !pushSupported()) { pushActivo.current = false; return; }
    isPushSubscribed().then((v) => { pushActivo.current = !!v; }).catch(() => {});
  }, [user]);

  // Web Push: si ya hay permiso, re-sincroniza la suscripción con el servidor
  // para recibir avisos con la pestaña o el navegador cerrados.
  useEffect(() => {
    if (!user) {
      pushEnsured.current = false;
      return;
    }
    if (pushEnsured.current) return;
    if (!pushSupported()) return;
    pushEnsured.current = true;
    ensurePushSubscription().catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    const { access } = getTokens();
    if (!access) return;
    const base = API_BASE || '';
    const wsUrl = base.replace(/^http/, 'ws') + `/api/ws?token=${encodeURIComponent(access)}`;
    let closed = false;
    let ws;
    let reconnectTimer;
    let latido;
    let attempt = 0;

    // Presencia: el servidor decide por dónde avisar (portal o sistema) según si
    // hay una pestaña abierta Y a la vista. Solo este navegador lo sabe.
    const enviar = (obj) => {
      try { if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify(obj)); } catch (e) {}
    };
    let ultimoEnviado = null;
    const enviarVisibilidad = () => {
      ultimoEnviado = atento();
      enviar({ type: 'visibility', visible: ultimoEnviado });
    };
    // La actividad solo se comunica cuando CAMBIA el estado (volver del descanso):
    // el latido ya cubre el resto sin inundar el socket con cada movimiento.
    const alActuar = () => {
      ultimaActividad = Date.now();
      if (ultimoEnviado === false && atento()) enviarVisibilidad();
    };
    const EVENTOS = ['pointerdown', 'pointermove', 'keydown', 'wheel', 'touchstart', 'scroll'];

    const connect = () => {
      if (closed) return;
      try { ws = new WebSocket(wsUrl); } catch (e) { return; }
      wsRef.current = ws;
      ws.onopen = () => {
        attempt = 0;
        setWsStatus('connected');
        enviarVisibilidad();
      };
      ws.onclose = () => {
        if (closed) return;
        setWsStatus('disconnected');
        // Reintento con backoff (pestaña en background a veces corta el WS).
        const delay = Math.min(30000, 1500 * (2 ** Math.min(attempt, 4)));
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => setWsStatus('disconnected');
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.type === 'new_mail') {
            pushNotification({
              title: data.from, subject: data.subject, uid: data.uid, type: 'new_mail',
              accountEmail: data.accountEmail, account_id: data.accountId, folder: data.folder || 'inbox',
            });
            toast(`Nuevo correo de ${data.from}`, {
              description: data.accountEmail ? `${data.subject} · ${data.accountEmail}` : data.subject,
              // El aviso lleva al correo (M29): antes había que buscarlo en la bandeja.
              ...(data.uid ? { action: { label: 'Abrir', onClick: () => abrirRef.current?.(data) } } : {}),
            });
            if (sonidoRef.current) playPing();
            // Banner del sistema: solo con la pestaña oculta Y sin Web Push. Con push,
            // el servidor ya sabe que no estamos a la vista y lo manda por el service
            // worker (que además abre el correo al pulsarlo). Este camino es el
            // respaldo para quien no tenga push activo.
            if (
              !pushActivo.current &&
              !atento() &&
              'Notification' in window &&
              Notification.permission === 'granted'
            ) {
              try {
                new Notification(`Nuevo correo de ${data.from}`, {
                  body: data.subject || '',
                  tag: `mail-${data.accountId || 'x'}-${data.uid || Date.now()}`,
                });
              } catch (e) {}
            }
            qc.invalidateQueries({ queryKey: ['unified'] });
            qc.invalidateQueries({ queryKey: ['unified-badge'] });
            qc.invalidateQueries({ queryKey: ['messages'] });
          } else if (data.type === 'mailbox_changed') {
            // El proveedor avisó (IMAP IDLE) y el servidor ya puso la caché al
            // día: correo que llegó ya leído, un leído o un borrado desde el
            // teléfono… Sin toast: solo se recarga lo que se ve.
            qc.invalidateQueries({ queryKey: ['unified'] });
            qc.invalidateQueries({ queryKey: ['unified-badge'] });
            qc.invalidateQueries({ queryKey: ['messages'] });
          }
        } catch (e) {}
      };
    };

    connect();
    latido = setInterval(enviarVisibilidad, LATIDO_MS);
    document.addEventListener('visibilitychange', enviarVisibilidad);
    window.addEventListener('focus', enviarVisibilidad);
    window.addEventListener('blur', enviarVisibilidad);
    EVENTOS.forEach((e) => window.addEventListener(e, alActuar, { passive: true, capture: true }));

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (latido) clearInterval(latido);
      document.removeEventListener('visibilitychange', enviarVisibilidad);
      window.removeEventListener('focus', enviarVisibilidad);
      window.removeEventListener('blur', enviarVisibilidad);
      EVENTOS.forEach((e) => window.removeEventListener(e, alActuar, { capture: true }));
      try { ws && ws.close(); } catch (e) {}
    };
  }, [user, pushNotification, qc, setWsStatus]);
}
